/**
 * AI メニュー開発の制約事項 管理画面 (user要望 2026-08-02)。
 * ブランド/店舗ごとに「必ず守るべき制約」(食材制限・予算・コンセプト・設備等) を登録する。
 * 登録内容は AI メニュー開発チャットの system prompt へ自動的に注入される
 * (apps/api/src/lib/anthropic/contexts/menu-dev.ts 参照)。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Save, X, ShieldAlert, AlertTriangle } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/Modal';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';

type Store = { store_id: string; store_name: string; status: 'active' | 'closed' };
type Constraint = {
  constraint_id: string;
  store_id: string | null;
  category: string;
  description: string;
  active_flg: boolean;
  created_at: string;
  updated_at: string;
};
type FormState = { store_id: string; category: string; description: string };

const CATEGORY_SUGGESTIONS = ['食材制限', '予算', 'コンセプト', '設備'];

export function AiMenuConstraintsPage() {
  const qc = useQueryClient();
  // ⚠️ バックエンドは既にmanage権限を要求しているが(POST/PATCH/DELETE /menu-dev/constraints)、
  // フロント側に対応する条件分岐が無く、view権限のみのユーザーにも新規作成・編集・削除・有効/無効
  // 切替のUIが常に表示されていた(2026-09-10、view権限のみのユーザーからの実機報告で発覚)。
  // 実際の操作はバックエンドが拒否するため権限昇格そのものは発生していなかったが、押せない操作を
  // 見せる設計は望ましくないため、manage権限の有無でUIを出し分ける。
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const canManage = hasPermissionForFeatureAction('ai_menu_constraints', 'manage');
  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });
  const { data: constraints = [], isLoading } = useQuery<Constraint[]>({
    queryKey: ['ai-menu-constraints'],
    queryFn: () => apiFetch<Constraint[]>('/menu-dev/constraints'),
  });
  const storeName = (id: string | null) => (id ? stores.find((s) => s.store_id === id)?.store_name ?? id : '全店舗共通');

  const [creating, setCreating] = useState<FormState | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState | null>(null);
  const [deleting, setDeleting] = useState<Constraint | null>(null);
  // デザインレビュー反映 (2026-08-02): 「有効→無効」は削除と同等の実質的な破壊力 (AI提案から
  // 直ちに外れる) があるため確認を挟む。「無効→有効」は安全な方向のため即時反映のままにする (H7)
  const [disabling, setDisabling] = useState<Constraint | null>(null);

  const createMut = useMutation({
    mutationFn: (body: FormState) => apiFetch('/menu-dev/constraints', {
      method: 'POST',
      body: JSON.stringify({ store_id: body.store_id || null, category: body.category.trim(), description: body.description.trim() }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-menu-constraints'] });
      setCreating(null);
      toast.success('制約事項を追加しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '追加に失敗しました'),
  });

  const updateMut = useMutation({
    mutationFn: (params: { id: string; body: Partial<FormState> & { active_flg?: boolean } }) =>
      apiFetch(`/menu-dev/constraints/${params.id}`, { method: 'PATCH', body: JSON.stringify(params.body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-menu-constraints'] });
      setEditingId(null);
      setDisabling(null);
      toast.success('制約事項を更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/menu-dev/constraints/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-menu-constraints'] });
      setDeleting(null);
      toast.success('制約事項を削除しました');
    },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleting(null); },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-h1 flex items-center gap-2"><ShieldAlert size={22} /> メニュー開発の制約事項</h1>
          <p className="text-sm text-neutral-sub mt-1">
            AIメニュー開発が必ず守るべき制約 (食材制限・予算・コンセプト・設備等) を登録します。
            有効な制約は、新メニュー提案・原価シミュレーション・既存メニュー改良のすべてに自動的に適用されます。
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setCreating({ store_id: '', category: '', description: '' })}
            className="inline-flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm font-medium whitespace-nowrap"
          >
            <Plus size={16} /> 新規制約事項
          </button>
        )}
      </div>

      {/* デザインレビュー反映 (2026-08-02): 一般マスタ画面と視覚的に無区別にならないよう、
          「有効な制約はAI提案に必ず適用される」という重要度を色・アイコン・文言の3重で強調する */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-warning-bg border border-warning-border text-warning-text text-sm">
        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
        <span>有効 (チェック済み) な制約事項は、AIメニュー開発の全ての提案に自動的に強制適用されます。無効化・削除すると次回の提案から直ちに適用されなくなります。</span>
      </div>

      {isLoading ? (
        <div className="text-sm text-neutral-sub">読込中…</div>
      ) : constraints.length === 0 ? (
        <EmptyState
          message="制約事項が登録されていません"
          description={canManage ? '「＋ 新規制約事項」から追加すると、次回以降のAI提案に自動的に反映されます' : undefined}
        />
      ) : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th scope="col" className="text-left px-4 py-2 w-32">適用範囲</th>
                <th scope="col" className="text-left px-4 py-2 w-32">カテゴリ</th>
                <th scope="col" className="text-left px-4 py-2">内容</th>
                <th scope="col" className="text-center px-4 py-2 w-20">有効</th>
                <th scope="col" className="w-24 px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {constraints.map((c) => {
                const isEditing = editingId === c.constraint_id;
                return (
                  <tr key={c.constraint_id} className={`border-t border-neutral-line ${!c.active_flg ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-2">
                      {isEditing && editForm ? (
                        <select
                          value={editForm.store_id}
                          onChange={(e) => setEditForm({ ...editForm, store_id: e.target.value })}
                          className="w-full px-2 py-1 rounded border border-neutral-line"
                        >
                          <option value="">全店舗共通</option>
                          {stores.filter((s) => s.status === 'active').map((s) => (
                            <option key={s.store_id} value={s.store_id}>{s.store_name}</option>
                          ))}
                        </select>
                      ) : storeName(c.store_id)}
                    </td>
                    <td className="px-4 py-2">
                      {isEditing && editForm ? (
                        <input
                          value={editForm.category}
                          onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                          list="constraint-category-suggestions"
                          className="w-full px-2 py-1 rounded border border-neutral-line"
                        />
                      ) : c.category}
                    </td>
                    <td className="px-4 py-2">
                      {isEditing && editForm ? (
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          rows={2}
                          className="w-full px-2 py-1 rounded border border-neutral-line"
                        />
                      ) : <span className="whitespace-pre-wrap">{c.description}</span>}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={c.active_flg}
                        disabled={!canManage}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateMut.mutate({ id: c.constraint_id, body: { active_flg: true } });
                          } else {
                            setDisabling(c);
                          }
                        }}
                        aria-label={c.active_flg ? '有効 (クリックで無効化)' : '無効 (クリックで有効化)'}
                      />
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {!canManage ? null : isEditing && editForm ? (
                        <>
                          <button type="button" aria-label="保存"
                            onClick={() => updateMut.mutate({ id: c.constraint_id, body: { store_id: editForm.store_id || null as unknown as string, category: editForm.category.trim(), description: editForm.description.trim() } })}
                            disabled={updateMut.isPending || editForm.category.trim() === '' || editForm.description.trim() === ''}
                            className="p-1 text-brand-red hover:text-brand-red-hover disabled:opacity-40">
                            <Save size={14} />
                          </button>
                          <button type="button" aria-label="キャンセル" onClick={() => setEditingId(null)} className="ml-1 p-1 text-neutral-sub hover:text-neutral-text">
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="button" aria-label="編集"
                            onClick={() => { setEditingId(c.constraint_id); setEditForm({ store_id: c.store_id ?? '', category: c.category, description: c.description }); }}
                            className="p-1 text-brand-red hover:text-brand-red-hover">
                            <Pencil size={14} />
                          </button>
                          <button type="button" aria-label="削除" onClick={() => setDeleting(c)} className="ml-1 p-1 text-neutral-sub hover:text-danger-text">
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <datalist id="constraint-category-suggestions">
        {CATEGORY_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
      </datalist>

      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCreating(null)}>
          <form onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              if (creating.category.trim() === '') { toast.error('カテゴリを入力してください'); return; }
              if (creating.description.trim() === '') { toast.error('内容を入力してください'); return; }
              createMut.mutate(creating);
            }}
            className="bg-white rounded-lg p-6 w-[480px] max-w-full space-y-3"
          >
            <h2 className="text-h3">新規制約事項</h2>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">適用範囲</label>
              <select
                value={creating.store_id}
                onChange={(e) => setCreating({ ...creating, store_id: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line"
              >
                <option value="">全店舗共通</option>
                {stores.filter((s) => s.status === 'active').map((s) => (
                  <option key={s.store_id} value={s.store_id}>{s.store_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">カテゴリ*</label>
              <input required autoFocus value={creating.category}
                onChange={(e) => setCreating({ ...creating, category: e.target.value })}
                list="constraint-category-suggestions"
                placeholder="例：食材制限、予算、コンセプト、設備"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">内容*</label>
              <textarea required value={creating.description}
                onChange={(e) => setCreating({ ...creating, description: e.target.value })}
                rows={4}
                placeholder="例：豚肉を使用したメニューは提案しない"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setCreating(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
              <button type="submit" disabled={createMut.isPending} className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
                {createMut.isPending ? '作成中…' : '作成'}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="制約事項を削除しますか？"
        destructive
        confirmLabel="削除する"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.constraint_id); }}
        message={deleting && (
          <div className="space-y-2">
            {/* デザインレビュー反映 (2026-08-02): 業務影響 (AI提案への即時反映) を明示 */}
            <p>以下の制約事項を削除します。<strong className="text-danger-text">削除すると次回のAI提案から直ちにこの制約が適用されなくなります</strong>。この操作は取り消せません。</p>
            <div className="p-3 rounded bg-neutral-panel border border-neutral-line text-sm space-y-1">
              <div><span className="text-neutral-sub">適用範囲：</span>{storeName(deleting.store_id)}</div>
              <div><span className="text-neutral-sub">カテゴリ：</span><span className="font-medium">{deleting.category}</span></div>
              <div><span className="text-neutral-sub">内容：</span>{deleting.description}</div>
            </div>
          </div>
        )}
      />

      {/* デザインレビュー反映 (2026-08-02): 「有効→無効」は削除と同等の業務影響があるため確認を挟む */}
      <ConfirmDialog
        open={disabling !== null}
        title="制約事項を無効化しますか？"
        destructive
        confirmLabel="無効化する"
        loading={updateMut.isPending}
        onClose={() => { if (!updateMut.isPending) setDisabling(null); }}
        onConfirm={() => { if (disabling) updateMut.mutate({ id: disabling.constraint_id, body: { active_flg: false } }); }}
        message={disabling && (
          <div className="space-y-2">
            <p>この制約事項を無効化します。<strong className="text-warning-text">次回のAI提案から直ちに適用されなくなります</strong>。いつでも再度有効化できます。</p>
            <div className="p-3 rounded bg-neutral-panel border border-neutral-line text-sm space-y-1">
              <div><span className="text-neutral-sub">適用範囲：</span>{storeName(disabling.store_id)}</div>
              <div><span className="text-neutral-sub">カテゴリ：</span><span className="font-medium">{disabling.category}</span></div>
              <div><span className="text-neutral-sub">内容：</span>{disabling.description}</div>
            </div>
          </div>
        )}
      />
    </div>
  );
}
